import React, { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Copy, Check, ChevronRight, ChevronDown, Database, Layers, Globe, Code2, FileText, Settings, Cloud, BookOpen } from "lucide-react";

const getTreeStructure = (objects, projectName) => {
  const name = projectName || "my-cap-app";
  return [
    {
      id: "cds",
      label: "CDS Files",
      icon: <Layers size={14} />,
      color: "text-blue-400",
      children: [
        objects.schema_cds && {
          id: "schema_cds",
          label: "db/schema.cds",
          icon: <Database size={13} />,
          color: "text-blue-300",
          lang: "sql",
          code: objects.schema_cds,
        },
        objects.service_cds && {
          id: "service_cds",
          label: "srv/service.cds",
          icon: <Layers size={13} />,
          color: "text-violet-300",
          lang: "sql",
          code: objects.service_cds,
        },
        objects.annotations_cds && {
          id: "annotations_cds",
          label: "srv/annotations.cds",
          icon: <FileText size={13} />,
          color: "text-pink-300",
          lang: "sql",
          code: objects.annotations_cds,
        },
      ].filter(Boolean),
    },
    {
      id: "handler",
      label: "Service Handler",
      icon: <Code2 size={14} />,
      color: "text-emerald-400",
      children: [
        objects.service_handler && {
          id: "service_handler",
          label: "srv/service.js",
          icon: <Code2 size={13} />,
          color: "text-emerald-300",
          lang: "javascript",
          code: objects.service_handler,
        },
      ].filter(Boolean),
    },
    {
      id: "config",
      label: "BTP Configuration",
      icon: <Settings size={14} />,
      color: "text-amber-400",
      children: [
        objects.package_json && {
          id: "package_json",
          label: "package.json",
          icon: <FileText size={13} />,
          color: "text-amber-300",
          lang: "json",
          code: objects.package_json,
        },
        objects.cdsrc_json && {
          id: "cdsrc_json",
          label: ".cdsrc.json",
          icon: <Settings size={13} />,
          color: "text-amber-300",
          lang: "json",
          code: objects.cdsrc_json,
        },
        objects.xs_security && {
          id: "xs_security",
          label: "xs-security.json",
          icon: <FileText size={13} />,
          color: "text-amber-300",
          lang: "json",
          code: objects.xs_security,
        },
        objects.mta_yaml && {
          id: "mta_yaml",
          label: "mta.yaml",
          icon: <Cloud size={13} />,
          color: "text-cyan-300",
          lang: "yaml",
          code: objects.mta_yaml,
        },
      ].filter(Boolean),
    },
    {
      id: "data",
      label: "Mock Data",
      icon: <Database size={14} />,
      color: "text-rose-400",
      children: [
        objects.csv_orders && {
          id: "csv_orders",
          label: "db/data/Orders.csv",
          icon: <FileText size={13} />,
          color: "text-rose-300",
          lang: "text",
          code: objects.csv_orders,
        },
        objects.csv_items && {
          id: "csv_items",
          label: "db/data/OrderItems.csv",
          icon: <FileText size={13} />,
          color: "text-rose-300",
          lang: "text",
          code: objects.csv_items,
        },
      ].filter(Boolean),
    },
    {
      id: "local",
      label: "Local Config",
      icon: <Settings size={14} />,
      color: "text-orange-400",
      children: [
        objects.gitignore && {
          id: "gitignore",
          label: ".gitignore",
          icon: <FileText size={13} />,
          color: "text-orange-300",
          lang: "text",
          code: objects.gitignore,
        },
        objects.env && {
          id: "env",
          label: ".env",
          icon: <Settings size={13} />,
          color: "text-orange-300",
          lang: "text",
          code: objects.env,
        },
      ].filter(Boolean),
    },
    {
      id: "docs",
      label: "Deploy Guide",
    },
  ].filter(n => n.children && n.children.length > 0);
};

const TreeNode = ({ node, selectedId, onSelect, depth = 0 }) => {
  const [open, setOpen] = useState(true);
  const isFolder = !!node.children;
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        onClick={() => {
          if (isFolder) setOpen(!open);
          else onSelect(node);
        }}
        className={`flex items-center gap-2 py-1.5 cursor-pointer rounded-lg transition-all text-left
          ${isSelected ? "bg-blue-600/30 border border-blue-500/40" : "hover:bg-white/5"}
        `}
        style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: "8px" }}
      >
        {isFolder && (
          <span className="text-white/30 shrink-0">
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        )}
        {!isFolder && <span className="w-3 shrink-0" />}
        <span className={`shrink-0 ${node.color}`}>{node.icon}</span>
        <span className={`text-[11px] font-bold truncate ${isSelected ? "text-white" : "text-white/60"}`}>
          {node.label}
        </span>
      </div>
      {isFolder && open && (
        <div>
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CapExplorer = ({ capObjects }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!capObjects?.objects) return null;

  const tree = getTreeStructure(capObjects.objects, capObjects.projectName);
  const totalObjects = Object.values(capObjects.objects).filter(Boolean).length;

  const handleCopy = () => {
    if (selectedNode?.code) {
      navigator.clipboard.writeText(selectedNode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyAll = () => {
    const all = Object.values(capObjects.objects)
      .filter(Boolean)
      .join("\n\n" + "─".repeat(60) + "\n\n");
    navigator.clipboard.writeText(all);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-6 rounded-[16px] overflow-hidden border border-white/10 shadow-2xl animate-in slide-in-from-bottom duration-700">
      {/* Header */}
      <div className="bg-white/5 px-6 py-3 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Cloud size={16} className="text-emerald-400" />
          <span className="text-[11px] font-black text-white uppercase tracking-widest">
            CAP Stack Explorer
          </span>
          <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 uppercase tracking-widest">
            {capObjects.projectName}
          </span>
          <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 uppercase tracking-widest">
            {totalObjects} archivos
          </span>
        </div>
        <button
          onClick={handleCopyAll}
          className="flex items-center gap-2 text-[10px] font-black text-white/40 hover:text-white uppercase tracking-widest transition-all"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          Copiar todo
        </button>
      </div>

      <div className="flex" style={{ minHeight: "520px" }}>
        {/* Tree Panel */}
        <div className="w-64 shrink-0 border-r border-white/8 bg-black/30 p-2 overflow-y-auto">
          <p className="text-[9px] font-black text-white/20 uppercase tracking-widest px-3 py-2">
            Archivos del Proyecto
          </p>
          {tree.map(node => (
            <TreeNode
              key={node.id}
              node={node}
              selectedId={selectedNode?.id}
              onSelect={setSelectedNode}
            />
          ))}
        </div>

        {/* Code Panel */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {selectedNode ? (
            <>
              <div className="flex items-center justify-between px-4 py-2 bg-black/40 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedNode(null)}
                    className="text-[9px] font-black text-white/30 hover:text-white uppercase tracking-widest transition-all mr-2"
                  >
                    ← Volver
                  </button>
                  <span className={selectedNode.color}>{selectedNode.icon}</span>
                  <span className="text-[11px] font-black text-white/60">{selectedNode.label}</span>
                </div>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 text-[10px] font-black text-blue-400 hover:text-white uppercase tracking-widest transition-all"
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                  Copiar
                </button>
              </div>
              <div className="flex-1 overflow-auto">
                <SyntaxHighlighter
                  language={selectedNode.lang || "javascript"}
                  style={vscDarkPlus}
                  customStyle={{
                    padding: "1.5rem",
                    fontSize: "12px",
                    margin: 0,
                    background: "#000000",
                    minHeight: "100%",
                  }}
                  wrapLines={true}
                  wrapLongLines={true}
                >
                  {selectedNode.code}
                </SyntaxHighlighter>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center flex-col gap-4">
              <div className="w-16 h-16 rounded-2xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center">
                <Cloud size={28} className="text-emerald-400/50" />
              </div>
              <div className="text-center">
                <p className="text-[12px] font-black text-white/30 uppercase tracking-widest mb-1">
                  Seleccioná un archivo
                </p>
                <p className="text-[10px] text-white/15 uppercase tracking-widest">
                  para ver su código
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-2 px-8">
                {tree.flatMap(folder => folder.children).map(node => (
                  <button
                    key={node.id}
                    onClick={() => setSelectedNode(node)}
                    className={`text-[9px] font-black px-2 py-1 rounded-lg border transition-all uppercase tracking-wide ${node.color} border-white/10 hover:border-white/30 bg-white/3 hover:bg-white/8`}
                  >
                    {node.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CapExplorer;